import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DirectEntryService } from '../../../../Services/direct-entry.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';

@Component({
  selector: 'app-direct-entry',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './direct-entry.component.html',
  styleUrl: './direct-entry.component.css'
})
export class DirectEntryComponent implements OnInit, OnDestroy {
  entries: any[] = [];
  paginatedEntries: any[] = [];
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  currentFirmId: number | null = null;
  currentFirmName: string = '';
  isEditing: boolean = false;
  editingEntryId: number | null = null;

  // Pagination
  p: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;

  // Form Fields
  entryDate: string = '';
  entryType: 'cash' | 'metal' = 'cash';
  personName: string = '';
  source: string = '';
  amount: number | null = null;
  grossWeight: number | null = null;
  purity: number | null = null;
  netWeight: number | null = null;

  private firmSubscription: Subscription | null = null;

  constructor(
    private directEntryService: DirectEntryService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit(): void {
    // Set initial date to today
    this.resetForm();

    // Subscribe to selected firm name/id
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.currentFirmId = firm.id;
        this.currentFirmName = firm.name;
        this.fetchEntries();
      }
    });

    // Check if there is already a selected firm on load
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.currentFirmId = currentFirm.id;
      this.currentFirmName = currentFirm.name;
      this.fetchEntries();
    }
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  fetchEntries(): void {
    if (!this.currentFirmId) return;
    this.isLoading = true;
    this.directEntryService.getDirectEntries({ firm_id: this.currentFirmId }).subscribe({
      next: (res: any) => {
        this.entries = res.data || [];
        this.isLoading = false;
        this.p = 1;
        this.filterTable();
      },
      error: (err) => {
        console.error('Error fetching entries:', err);
        this.isLoading = false;
      }
    });
  }

  filterTable(): void {
    this.totalPages = Math.ceil(this.entries.length / this.itemsPerPage);
    if (this.p > this.totalPages && this.totalPages > 0) {
      this.p = this.totalPages;
    }
    const start = (this.p - 1) * this.itemsPerPage;
    this.paginatedEntries = this.entries.slice(start, start + this.itemsPerPage);
  }

  onPageChange(page: number): void {
    this.p = page;
    this.filterTable();
  }

  onItemsPerPageChange(): void {
    this.p = 1;
    this.filterTable();
  }

  get pageNumbers(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.p;
    const maxPagesToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        startPage = 2;
        endPage = 3;
      }
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  }

  goToPreviousPage(): void {
    if (this.p > 1) {
      this.onPageChange(this.p - 1);
    }
  }

  goToNextPage(): void {
    if (this.p < this.totalPages) {
      this.onPageChange(this.p + 1);
    }
  }

  calculateNetWeight(): void {
    if (this.grossWeight !== null && this.purity !== null) {
      this.netWeight = parseFloat((this.grossWeight * this.purity / 100).toFixed(3));
    } else {
      this.netWeight = null;
    }
  }

  onSubmit(): void {
    if (!this.currentFirmId) {
      alert('Please select a firm first.');
      return;
    }
    if (!this.personName || !this.entryDate) {
      alert('Please fill out all required fields.');
      return;
    }
    if (this.entryType === 'cash' && (this.amount === null || this.amount <= 0)) {
      alert('Please enter a valid cash amount.');
      return;
    }
    if (this.entryType === 'metal' && (this.grossWeight === null || this.grossWeight <= 0 || this.purity === null || this.purity <= 0)) {
      alert('Please enter valid metal weight and purity.');
      return;
    }

    const payload: any = {
      firm_id: this.currentFirmId,
      entry_date: this.entryDate,
      entry_type: this.entryType,
      person_name: this.personName,
      source: this.source
    };

    if (this.entryType === 'cash') {
      payload.amount = this.amount;
    } else {
      payload.gross_weight = this.grossWeight;
      payload.purity = this.purity;
      payload.net_weight = this.netWeight;
    }

    this.isSubmitting = true;
    if (this.isEditing && this.editingEntryId !== null) {
      this.directEntryService.updateDirectEntry(this.editingEntryId, payload).subscribe({
        next: (res: any) => {
          alert('Direct entry updated successfully!');
          this.resetForm();
          this.fetchEntries();
          this.isSubmitting = false;
        },
        error: (err: any) => {
          console.error('Error updating entry:', err);
          const errMsg = err.error?.message || 'Error occurred while updating direct entry.';
          alert(errMsg);
          this.isSubmitting = false;
        }
      });
    } else {
      this.directEntryService.createDirectEntry(payload).subscribe({
        next: (res: any) => {
          alert('Direct entry recorded successfully!');
          this.resetForm();
          this.fetchEntries();
          this.isSubmitting = false;
        },
        error: (err: any) => {
          console.error('Error creating entry:', err);
          const errMsg = err.error?.message || 'Error occurred while saving direct entry.';
          alert(errMsg);
          this.isSubmitting = false;
        }
      });
    }
  }

  resetForm(): void {
    this.entryDate = new Date().toISOString().split('T')[0];
    this.entryType = 'cash';
    this.personName = '';
    this.source = '';
    this.amount = null;
    this.grossWeight = null;
    this.purity = null;
    this.netWeight = null;
    this.isEditing = false;
    this.editingEntryId = null;
  }

  editEntry(entry: any): void {
    this.isEditing = true;
    this.editingEntryId = entry.id;
    this.entryDate = entry.entry_date;
    this.entryType = entry.entry_type;
    this.personName = entry.person_name;
    this.source = entry.source || '';
    if (entry.entry_type === 'cash') {
      this.amount = entry.amount;
      this.grossWeight = null;
      this.purity = null;
      this.netWeight = null;
    } else {
      this.amount = null;
      this.grossWeight = entry.gross_weight;
      this.purity = entry.purity;
      this.netWeight = entry.net_weight;
    }
  }

  deleteEntry(id: number): void {
    if (confirm('Are you sure you want to delete this entry? This will also remove the associated ledger transactions.')) {
      this.directEntryService.deleteDirectEntry(id).subscribe({
        next: (res: any) => {
          alert('Entry deleted successfully!');
          this.fetchEntries();
        },
        error: (err) => {
          console.error('Error deleting entry:', err);
          alert(err.error?.message || 'Failed to delete direct entry.');
        }
      });
    }
  }

  printReceipt(entry: any): void {
    const firmName = this.currentFirmName || 'Meena Silver NG';
    const formattedDate = entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('en-GB') : 'N/A';
    const generatedOn = new Date().toLocaleString('en-GB');

    let detailsHtml = '';
    if (entry.entry_type === 'cash') {
      detailsHtml = `
        <div class="row">
          <span class="label">Entry Type:</span>
          <span class="value">Cash</span>
        </div>
        <div class="row amt-row">
          <span class="label">Amount:</span>
          <span class="value">₹ ${parseFloat(entry.amount).toFixed(2)}</span>
        </div>
      `;
    } else {
      detailsHtml = `
        <div class="row">
          <span class="label">Entry Type:</span>
          <span class="value">Metal</span>
        </div>
        <div class="row">
          <span class="label">Gross Weight:</span>
          <span class="value">${parseFloat(entry.gross_weight).toFixed(3)} gm</span>
        </div>
        <div class="row">
          <span class="label">Purity:</span>
          <span class="value">${parseFloat(entry.purity).toFixed(2)}%</span>
        </div>
        <div class="row amt-row">
          <span class="label">Net Weight:</span>
          <span class="value">${parseFloat(entry.net_weight).toFixed(3)} gm</span>
        </div>
      `;
    }

    const printContent = `
    <html>
    <head>
      <title>Receipt - Direct Cash/Metal Entry</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 40px; color: #333; line-height: 1.5; background-color: #fff; }
        .receipt-card { max-width: 480px; margin: 0 auto; border: 1px solid #e0e0e0; padding: 30px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .header { text-align: center; border-bottom: 2px solid #0d5bab; padding-bottom: 15px; margin-bottom: 25px; }
        .header h1 { margin: 0; font-size: 24px; color: #0d5bab; font-weight: 700; text-transform: uppercase; }
        .header p { margin: 5px 0 0 0; font-size: 14px; color: #666; }
        .title { text-align: center; font-size: 16px; font-weight: bold; letter-spacing: 1px; color: #555; text-transform: uppercase; margin-bottom: 25px; }
        
        .row { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 14px; border-bottom: 1px dashed #f0f0f0; padding-bottom: 6px; }
        .row:last-child { border-bottom: none; }
        .label { color: #777; font-weight: 500; }
        .value { color: #111; font-weight: 600; }
        
        .amt-row { border-top: 2px solid #eee; border-bottom: 2px solid #eee; padding: 12px 0; margin-top: 20px; margin-bottom: 20px; }
        .amt-row .label { color: #0d5bab; font-size: 16px; font-weight: bold; }
        .amt-row .value { color: #0d5bab; font-size: 20px; font-weight: 800; }
        
        .sign-section { display: flex; justify-content: space-between; margin-top: 60px; }
        .signature { border-top: 1px solid #ccc; width: 150px; text-align: center; padding-top: 5px; font-size: 12px; color: #666; }
        
        .footer { text-align: center; font-size: 10px; color: #999; margin-top: 40px; border-top: 1px solid #eee; padding-top: 15px; }
        
        @media print {
          body { padding: 0; background-color: transparent; }
          .receipt-card { border: none; box-shadow: none; max-width: 100%; padding: 10px; }
        }
      </style>
    </head>
    <body>
      <div class="receipt-card">
        <div class="header">
          <!-- <h1>${firmName}</h1> -->
          <p>Direct Entry Receipt</p>
        </div>
        
        <div class="title">Receipt Acknowledgement</div>
        
        <div class="row">
          <span class="label">Receipt No:</span>
          <span class="value">#DE-${entry.id}</span>
        </div>
        <div class="row">
          <span class="label">Date:</span>
          <span class="value">${formattedDate}</span>
        </div>
        <div class="row">
          <span class="label">Received From:</span>
          <span class="value">${entry.person_name}</span>
        </div>
        <div class="row">
          <span class="label">Source:</span>
          <span class="value">${entry.source || 'N/A'}</span>
        </div>
        
        ${detailsHtml}
        
        <div class="sign-section">
          <div class="signature">Depositor Signature</div>
          <div class="signature">Authorized Signatory</div>
        </div>
        
        <div class="footer">
          Thank you for choosing the Meena Silver. Generated on ${generatedOn}
        </div>
      </div>
      
      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() { window.close(); };
        }
      </script>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  }
}
